'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { CalendarClock, Clock, Lock, MapPin, ShieldCheck, X } from 'lucide-react'
import type { BookingProduct } from '@sifly/shared-types'
import { formatCents } from '@sifly/shared-utils'
import PhoneInput from '@sifly/shared-ui/content/LazyPhoneInput'
import { BookingCalendar } from './BookingCalendar'
import { QtyStepper } from './QtyStepper'
import { LocationMap, type Pickup } from './LocationMap'
import { locationKindCopy } from '@/lib/locationKind'
import { addMonths, monthStartOfYmd, type DayAvailability } from './_lib/bookingCalendarModel'
import { bookingDays, toExclusiveEnd } from './_lib/bookingDays'
import { clampQty } from './_lib/bookingQuantity'
import { priceBreakdown } from './_lib/bookingPriceBreakdown'
import { bookingTaxDisplay } from './_lib/bookingTaxDisplay'
import { formatRangeLabel, rangeParts } from './_lib/bookingDateRange'
import { startCheckout, type CheckoutResult } from './_lib/checkoutClient'
import { confirmBookingNow } from './_lib/confirmActions'
import { useBookingContact } from './_lib/useBookingContact'
import { formChrome } from './_lib/formLayout'
import { useStepSheetModal } from './_lib/useStepSheetModal'
import { BookingFlowRunner } from '../../_flow/BookingFlowRunner'
import { BookingLinkKeepsake } from './BookingLinkKeepsake'
import PartnerStrip from '@/components/checkout/PartnerStrip'
import ReferralQuestion from '@/components/checkout/ReferralQuestion'

// PaymentStep (Stripe Elements) is browser-only and reached only in the pay
// phase — code-split it so the initial PDP bundle skips the chunk until the
// visitor gets there.
const PaymentStep = dynamic(() => import('./PaymentStep').then(m => m.PaymentStep), {
  ssr: false,
  loading: () => (
    <div className="py-8 text-center text-sm text-[var(--color-text-secondary)]">Preparing secure payment…</div>
  ),
})

type Props = {
  product: BookingProduct & { bookable_resources: { id: string; name: string; capacity: number } | null }
  stripeAccountId: string | null
  /** The dealer's published waiver terms (prose markdown), shown in the signing
   *  surface's scroll-to-read box. Null when the dealer hasn't published a
   *  waiver page (the signature pad isn't rendered in that case). */
  waiverTerms?: string | null
  /** Dealer minors policy + floor (tenants.allow_minors / min_rider_age). */
  allowMinors?: boolean
  minRiderAge?: number
  /** Resolved product hero image (CF Images URL + alt), or null. */
  heroImage?: { url: string; alt: string | null } | null
  /** Resolved pickup location (name + locality + coords + directions), or null. */
  pickup?: Pickup | null
  /** Public tenant phone (null when the dealer hides it). Powers the
   *  call-to-book escape hatch when the calendar has nothing open. */
  phone?: string | null
  /** 'full' = the /bookings page (default, unchanged). 'embed' = booking_widget
   *  content section: selection inline, checkout phases in the overlay. */
  layout?: 'full' | 'embed'
  /** Today in the pickup location's zone, 'YYYY-MM-DD', resolved on the
   *  server (bookingProductContext). The opening month and the past-day floor
   *  both derive from it. Never read the clock here: the server builds this
   *  form on UTC and the visitor's browser rebuilds it on their zone, and one
   *  differing month is a hydration mismatch that duplicates the page's
   *  inline JSON-LD. */
  today: string
  /** Sales tax percent resolved server-side (bookingProductContext), or null. */
  taxRatePct?: number | null
  /** True when the server couldn't resolve tax inputs — show "added at
   *  checkout" instead of claiming a tax-inclusive total. */
  taxUnknown?: boolean
}
type Phase = 'choose' | 'contact' | 'pay' | 'confirmed'

export function BookingForm({ product, stripeAccountId, waiverTerms, allowMinors = false, minRiderAge = 12, heroImage, pickup, phone, layout = 'full', today, taxRatePct = null, taxUnknown = false }: Props) {
  const [month, setMonth] = useState(() => monthStartOfYmd(today))
  // Stable for the session — the location-local date is the calendar's floor.
  const [minDate] = useState(today)
  const [daysByMonth, setDaysByMonth] = useState<Record<string, DayAvailability[]>>({})
  const [calLoading, setCalLoading] = useState(true)
  const [range, setRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null })
  const [price, setPrice] = useState<{ totalCents: number; days: number; perDayCents: number } | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [qty, setQty] = useState(1)
  const [phase, setPhase] = useState<Phase>('choose')
  // Contact fields persist to sessionStorage (tenant-scoped) so a card decline,
  // reload, or app-switch mid-checkout doesn't wipe them — restored on mount,
  // cleared on a confirmed booking. See useBookingContact / bookingContactStorage.
  const { contact, setContact, returnedAfterFailure, markPaymentFailed, clear: clearBookingContact, dismissReturnBanner } =
    useBookingContact(product.tenant_id)
  const [checkout, setCheckout] = useState<CheckoutResult | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Raw durable flow token from confirmBookingNow — powers the keepsake link on
  // the confirmation screen. null until a confirmed mint returns one.
  const [flowToken, setFlowToken] = useState<string | null>(null)

  // Load the anchor month + the next month (the dual-month calendar shows both).
  // Always fetch with qty=1 so `remaining` is the true per-day unit count; the
  // selected qty disables cells client-side (decoupled from the RPC's qty arg).
  const loadMonths = useCallback(async (m: string) => {
    setCalLoading(true)
    const fetchOne = async (mm: string): Promise<[string, DayAvailability[]]> => {
      const res = await fetch(`/api/bookings/availability?productId=${product.id}&month=${mm}&qty=1`)
      const json = (await res.json().catch(() => ({}))) as { days?: DayAvailability[] }
      return [mm, Array.isArray(json.days) ? json.days : []]
    }
    const entries = await Promise.all([fetchOne(m), fetchOne(addMonths(m, 1))])
    setDaysByMonth(prev => ({ ...prev, ...Object.fromEntries(entries) }))
    setCalLoading(false)
  }, [product.id])

  useEffect(() => { loadMonths(month) }, [month, loadMonths])

  // Price the selected range and capture the unit cap (`remaining`).
  useEffect(() => {
    if (!range.start || !range.end) { setPrice(null); setRemaining(null); return }
    // Capture before the async closure (narrowing is lost across the await).
    // range.end is the INCLUSIVE drop-off day; the API speaks the exclusive
    // checkout boundary, so convert here. end − start then = inclusive days.
    const start = range.start
    const endExclusive = toExclusiveEnd(range.end)
    let cancelled = false
    ;(async () => {
      const res = await fetch(
        `/api/bookings/availability?productId=${product.id}&start=${start}&end=${endExclusive}&qty=1`,
      )
      const json = (await res.json().catch(() => ({}))) as {
        available?: boolean
        remaining?: number
        price?: { totalCents: number; days: number; perDayCents: number }
      }
      if (cancelled) return
      const max = typeof json.remaining === 'number' ? json.remaining : 0
      setRemaining(max)
      setQty(q => clampQty(q, max) || 1)
      setPrice(json.price && max > 0 ? json.price : null)
    })()
    return () => { cancelled = true }
  }, [range, product.id])

  const reserve = async () => {
    if (!range.start || !range.end) return
    setNotice(null)
    const r = await startCheckout({
      productId: product.id,
      start: range.start,
      end: toExclusiveEnd(range.end), // inclusive drop-off → exclusive checkout boundary
      qty,
      contact,
      releaseBookingId: checkout?.bookingId,
    })
    if (!r.ok) {
      if (r.status === 409) {
        setNotice('Those dates were just taken — pick another.')
        await loadMonths(month)
        setRange({ start: null, end: null })
      } else {
        setNotice(r.error)
      }
      return
    }
    setCheckout(r.data)
    setPhase('pay')
  }

  const onPaid = async (piId: string) => {
    if (!checkout) return
    const res = await confirmBookingNow({
      bookingId: checkout.bookingId,
      paymentIntentId: piId,
      stripeAccountId: checkout.stripeAccountId,
    })
    // Durable link for the confirmation screen (safety net; absent if the mint
    // failed — the confirmation email remains the backstop).
    if (res.flowToken) setFlowToken(res.flowToken)
    // Booked — the contact fields have done their job; drop the saved copy so a
    // later visit starts clean (and no stale payment-failed flag lingers).
    clearBookingContact()
    setPhase('confirmed')
  }

  const onExpire = useCallback(() => {
    setCheckout(null)
    setPhase('choose')
    setRange({ start: null, end: null })
    setQty(1)
    loadMonths(month)
  }, [month, loadMonths])

  const rangeLabel = formatRangeLabel(range.start, range.end)
  const parts = rangeParts(range.start, range.end)
  const policy = (product.cancellation_policy ?? {}) as { full_refund_hours?: number }
  const breakdown = price
    ? priceBreakdown({ perUnitTotalCents: price.totalCents, perDayCents: price.perDayCents, days: price.days, qty })
    : null
  const taxDisplay = breakdown
    ? bookingTaxDisplay(breakdown.grandTotalCents, taxRatePct, product.tax_treatment)
    : null
  const chrome = formChrome({ layout, phase, hasSelection: range.start != null })
  const rootRef = useRef<HTMLDivElement>(null)
  const overlayRef = useStepSheetModal({
    open: phase !== 'choose',
    allBreakpoints: chrome.overlayAllBreakpoints,
    onClose: onExpire,
    returnFocusRef: rootRef,
  })

  // Trust / details facts, FareHarbor-style — built only from real product fields.
  // Each row leads with a lucide icon so the page scans as glanceable facts, not
  // a wall of label/value text.
  type Detail = { icon: ComponentType<{ className?: string }>; label: string; value: string }
  const details: Detail[] = []
  // The resolved pickup, NOT the resource name — see the matching comment in
  // SessionBookingForm. A resource is a fleet, not a place, and since migration
  // 357 it need not be at the place customers are actually met.
  if (pickup) {
    // Label follows the location KIND. 'Pickup' is right for a rental collected
    // from a shop and wrong for one handed over lakeside; hardcoding it made the
    // row assert the opposite of the /bookings heading. See locationKind.ts.
    details.push({
      icon: MapPin,
      label: locationKindCopy(pickup.kind ?? 'unknown').detailLabel,
      value: pickup.shortName ?? pickup.name,
    })
  }
  // Once a complete range is picked, show the selected inclusive day count;
  // before that, fall back to the product's allowed min–max range.
  const selectedDays = range.start && range.end ? bookingDays(range.start, range.end) : null
  details.push({
    icon: Clock,
    label: 'Duration',
    value: selectedDays != null
      ? `${selectedDays} day${selectedDays === 1 ? '' : 's'}`
      : product.min_days === product.max_days
        ? `${product.min_days} day${product.min_days === 1 ? '' : 's'}`
        : `${product.min_days}–${product.max_days} days`,
  })
  if (policy.full_refund_hours != null) {
    details.push({ icon: ShieldCheck, label: 'Cancellation', value: `Free up to ${policy.full_refund_hours}h before pickup` })
  }
  if (product.damage_hold_cents > 0) {
    // Per-unit hold × selected qty — matches what booking-actions.ts actually
    // authorizes at pickup, so a qty>1 customer isn't surprised by a bigger hold.
    details.push({ icon: Lock, label: 'Damage hold', value: `Refundable ${formatCents(product.damage_hold_cents * qty)} authorized at pickup` })
  }
  if (product.lead_time_days > 0) {
    details.push({ icon: CalendarClock, label: 'Lead time', value: `Book at least ${product.lead_time_days} day${product.lead_time_days === 1 ? '' : 's'} ahead` })
  }

  // ── Booking panel (right col on desktop; step-sheet body on mobile) ──
  const panel = (
    <div className="space-y-5">
      {/* Return-after-decline banner — calm warning token, explains why the form
          is prefilled after a failed payment. One-shot per return; auto-clears on
          a confirmed booking, dismissible meanwhile. */}
      {returnedAfterFailure && phase !== 'confirmed' && (
        <div className="flex items-start gap-3 rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-surface)] px-4 py-3 text-sm text-[var(--color-warning-strong)]">
          <p className="flex-1">Your payment didn&apos;t go through — your details are saved. Try another card.</p>
          <button
            type="button"
            onClick={dismissReturnBanner}
            aria-label="Dismiss"
            className="-mr-1 shrink-0 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Selected dates — an Airbnb-style two-field Pickup / Return header,
          shown the moment a pickup day is tapped, so the choice survives paging
          months on mobile (the calendar highlight scrolls out of view; this
          doesn't). Weekday-stamped so a wrong day of week is catchable. */}
      {parts && (
        <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-[var(--color-border)]">
          <div className="p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Pickup</p>
            <p className="mt-0.5 text-sm font-medium text-[var(--color-text-primary)]">{parts.pickup}</p>
          </div>
          <div className="border-l border-[var(--color-border)] p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Return</p>
            <p className="mt-0.5 text-sm font-medium text-[var(--color-text-primary)]">
              {parts.return ?? <span className="text-[var(--color-text-muted)]">Select date</span>}
            </p>
          </div>
        </div>
      )}

      {/* Price — always at top of panel */}
      {breakdown ? (
        <div>
          <p className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {formatCents(taxDisplay!.totalCents)}{' '}
            <span className="text-sm font-normal text-[var(--color-text-muted)]">
              · {price!.days} day{price!.days === 1 ? '' : 's'}
            </span>
          </p>
          <ul className="mt-2 space-y-1">
            {breakdown.lines.map((l, i) => (
              <li key={i} className="flex justify-between text-sm text-[var(--color-text-muted)]">
                <span>{l.label}</span>
                <span>{formatCents(l.amountCents)}</span>
              </li>
            ))}
            {taxUnknown ? (
              <li className="text-sm text-[var(--color-text-muted)]">
                Sales tax, if any, is added at checkout.
              </li>
            ) : (
              taxDisplay?.taxLine && (
                <li className="flex justify-between text-sm text-[var(--color-text-muted)]">
                  <span>{taxDisplay.taxLine.label}</span>
                  <span>{formatCents(taxDisplay.taxLine.amountCents)}</span>
                </li>
              )
            )}
          </ul>
        </div>
      ) : (
        <p className="text-[var(--color-text-muted)]">Select dates to see the price</p>
      )}

      {/* choose: calendar + quantity + Reserve */}
      {phase === 'choose' && (
        <>
          <BookingCalendar
            monthStart={month}
            daysByMonth={daysByMonth}
            qty={qty}
            selected={range}
            minDate={minDate}
            loading={calLoading}
            onRangeChange={(s, e) => setRange({ start: s, end: e })}
            onMonthChange={setMonth}
            hint={range.start && !range.end ? 'Now select your return date' : 'Select a pickup date, then your drop-off date'}
            phone={phone}
          />
          {remaining != null && remaining >= 2 && (
            <QtyStepper value={qty} max={remaining} onChange={n => setQty(clampQty(n, remaining))} />
          )}
          {notice && <p className="text-sm text-[var(--color-text-secondary)]">{notice}</p>}
          <button
            type="button"
            disabled={!price}
            onClick={() => setPhase('contact')}
            className="w-full rounded-md bg-[var(--color-cta)] py-3 text-[var(--color-cta-text)] font-medium disabled:opacity-60"
          >
            Reserve
          </button>
        </>
      )}

      {/* contact: email + name + phone → startCheckout */}
      {phase === 'contact' && (
        <form onSubmit={e => { e.preventDefault(); void reserve() }} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="bk-first" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">First name</label>
              <input
                id="bk-first" required autoComplete="given-name" placeholder="Duke"
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
                value={contact.firstName}
                onChange={e => setContact({ ...contact, firstName: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="bk-last" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Last name</label>
              <input
                id="bk-last" required autoComplete="family-name" placeholder="Kahanamoku"
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
                value={contact.lastName}
                onChange={e => setContact({ ...contact, lastName: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label htmlFor="bk-email" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Email</label>
            <input
              id="bk-email" required type="email" autoComplete="email" placeholder="you@example.com"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
              value={contact.email}
              onChange={e => setContact({ ...contact, email: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="bk-phone" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Phone</label>
            <PhoneInput
              id="bk-phone"
              value={contact.phone}
              onChange={e164 => setContact(c => ({ ...c, phone: e164 }))}
              defaultCountry="US"
              favoriteCountries={['US', 'CA']}
              required
              placeholder="(555) 123-4567"
            />
          </div>
          {notice && <p className="text-sm text-[var(--color-text-secondary)]">{notice}</p>}
          <button type="submit" className="w-full rounded-md bg-[var(--color-cta)] py-3 text-[var(--color-cta-text)] font-medium">
            Continue to payment
          </button>
        </form>
      )}

      {/* pay: Payment Element + countdown */}
      {phase === 'pay' && checkout && (
        <>
          <div className="mb-3 empty:hidden"><PartnerStrip /></div>
          <PaymentStep checkout={checkout} onSucceeded={onPaid} onExpire={onExpire} onPaymentError={markPaymentFailed} />
        </>
      )}

      {/* confirmed: booking summary + the pinned post-checkout flow */}
      {phase === 'confirmed' && checkout && (
        <div className="space-y-4">
          <div className="rounded-md border border-[var(--color-border)] p-4">
            <p className="font-medium text-[var(--color-text-primary)]">You&apos;re booked!</p>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              {rangeLabel}
            </p>
          </div>
          <BookingFlowRunner
            flow={checkout.flow}
            capability={{
              kind: 'pi',
              bookingId: checkout.bookingId,
              paymentIntentId: checkout.paymentIntentId,
              stripeAccountId: checkout.stripeAccountId,
            }}
            booking={{ mode: 'daily', requiredRiders: qty, signedSlots: [] }}
            waiverTerms={waiverTerms ?? null}
            policy={{ minRiderAge, allowMinors }}
          />
          {flowToken && <BookingLinkKeepsake token={flowToken} />}
          <div className="empty:hidden"><ReferralQuestion kind="booking" saleId={checkout.bookingId} /></div>
        </div>
      )}
    </div>
  )

  const priceLabel = breakdown
    ? `${formatCents(taxDisplay!.totalCents)} · ${price!.days} day${price!.days === 1 ? '' : 's'}`
    : 'Select dates'

  // Left column: hero image, title/description, and a details list.
  const overview = (
    <div>
      {heroImage && (
        <div className="relative mb-6 w-full aspect-[4/3] overflow-hidden rounded-lg">
          <Image
            src={heroImage.url}
            alt={heroImage.alt ?? product.title}
            fill
            priority
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="object-cover"
          />
        </div>
      )}
      <h1 className="text-3xl font-semibold text-[var(--color-text-primary)]">{product.title}</h1>
      {product.description && (
        <p className="mt-3 text-[var(--color-text-secondary)]">{product.description}</p>
      )}
      {details.length > 0 && (
        <ul className="mt-6 space-y-4 border-t border-[var(--color-border)] pt-6">
          {details.map(d => (
            <li key={d.label} className="flex items-start gap-3 text-sm">
              <d.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-cta-readable)]" aria-hidden="true" />
              <div>
                <p className="font-medium text-[var(--color-text-primary)]">{d.value}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{d.label}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  // ── No Stripe connected account — calm pre-flight guard ──────────────────────
  if (!stripeAccountId) {
    return (
      <div className={chrome.pageChrome ? 'max-w-6xl mx-auto px-4 py-12' : undefined}>
        <div className={chrome.showOverview ? 'grid grid-cols-1 lg:grid-cols-2 gap-12' : undefined}>
          {chrome.showOverview && overview}
          <div className="rounded-md border border-[var(--color-border)] p-6 space-y-2">
            <p className="font-medium text-[var(--color-text-primary)]">
              Online booking isn&apos;t available for this location yet.
            </p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Contact the dealer directly to reserve.
            </p>
          </div>
        </div>
        {chrome.showMap && pickup && <LocationMap pickup={pickup} />}
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      // Focus target when the step sheet closes (see useStepSheetModal). Not a
      // Tab stop, so the ring it would paint around the whole form is noise.
      tabIndex={-1}
      className={
        chrome.pageChrome
          ? 'focus:outline-none max-w-6xl mx-auto px-4 pt-12 pb-28 lg:pb-12'
          : chrome.showStickyBar
            ? 'focus:outline-none pb-28 lg:pb-0' // room for the fixed sticky bar on mobile
            : 'focus:outline-none'
      }
    >
      <div className={chrome.showOverview ? 'grid grid-cols-1 lg:grid-cols-2 gap-12' : undefined}>
        {/* Left: hero + title + description + details */}
        {chrome.showOverview && overview}

        {/* Booking panel in the page flow. 'full': hidden on mobile once the
            step sheet takes over (historical behavior). 'embed': only mounted
            during the choose phase — checkout lives in the overlay. */}
        {chrome.showDesktopInlinePanel && (
          <div className={phase !== 'choose' && chrome.pageChrome ? 'hidden lg:block' : undefined}>{panel}</div>
        )}
      </div>

      {/* Pickup location map — full-width, Turo-style, under the fold */}
      {chrome.showMap && pickup && <LocationMap pickup={pickup} />}

      {/* ── Mobile sticky CTA bar (choose phase; embed: only once a date is picked) ── */}
      {chrome.showStickyBar && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 border-t border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                {rangeLabel ?? 'Select your dates'}
              </p>
              <p className="truncate text-xs text-[var(--color-text-muted)]">
                {price ? priceLabel : range.start ? 'Tap your return date' : 'Pickup & return on the calendar'}
              </p>
            </div>
            <button
              type="button"
              disabled={!price}
              onClick={() => setPhase('contact')}
              className="flex-shrink-0 rounded-md bg-[var(--color-cta)] px-5 py-2.5 text-sm font-medium text-[var(--color-cta-text)] disabled:opacity-60"
            >
              Reserve
            </button>
          </div>
        </div>
      )}

      {/* ── Step-sheet overlay (contact / pay / confirmed). 'full': mobile only.
          'embed': all breakpoints — the focused checkout surface. ── */}
      {phase !== 'choose' && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="Complete your booking"
          className={`${chrome.overlayAllBreakpoints ? '' : 'lg:hidden '}fixed inset-0 z-50 bg-[var(--color-bg,#fff)] overflow-y-auto px-4 py-6`}
        >
          {(() => {
            const body = (
              <>
                <button
                  type="button"
                  aria-label="Close and release hold"
                  onClick={onExpire}
                  className="mb-6 flex items-center gap-1 text-sm text-[var(--color-text-secondary)]"
                >
                  <span aria-hidden="true">‹</span> Back
                </button>
                {panel}
              </>
            )
            return chrome.overlayAllBreakpoints ? <div className="mx-auto w-full max-w-lg">{body}</div> : body
          })()}
        </div>
      )}
    </div>
  )
}
